import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  formatDisplayedDate,
  formatRangeLabel,
  formatValueDate,
} from "./datePickerUtils";

const useDatePickerState = ({
  value,
  isDisabled,
  isViewMode,
  isLoading,
  defaultValue,
  variant = "single",
  formatDisplay,
  formatValue,
  availableDates,
  onChange,
}) => {
  const [innerValue, setInnerValue] = useState(defaultValue);
  const [displayedText, setDisplayedText] = useState("");
  const [displayedRangeText, setDisplayedRangeText] = useState("");

  const [anchorEl, setAnchorEl] = useState(null);
  const isOpen = Boolean(anchorEl);

  const defaultMonth = useMemo(() => {
    if (variant === "range") {
      if (value) {
        return value.from ?? value.to;
      }
      if (innerValue) {
        return innerValue.from ?? innerValue.to;
      }
      return;
    }
    return value ?? innerValue;
  }, [value, innerValue, variant]);

  useEffect(() => {
    const valueToDisplay = value ?? innerValue;

    if (variant === "range") {
      setDisplayedRangeText(
        formatRangeLabel(valueToDisplay?.from, valueToDisplay?.to)
      );
      return;
    }
    setDisplayedText(
      valueToDisplay ? formatDisplayedDate(valueToDisplay, formatDisplay) : ""
    );
  }, [value, innerValue, variant, formatDisplay]);

  const handleOpen = (event) => {
    if (isDisabled || isViewMode || isLoading) return;

    if (!isOpen) {
      setAnchorEl(event.currentTarget);
    }
  };
  const handleClose = () => setAnchorEl(null);

  const handleChange = (newValue, _, metadata) => {
    let finalValue = newValue;
    if (formatValue) {
      if (variant === "range") {
        finalValue = {
          from: newValue?.from
            ? formatValueDate(newValue.from, formatValue)
            : undefined,
          to: newValue?.to
            ? formatValueDate(newValue.to, formatValue)
            : undefined,
        };
      } else {
        finalValue = newValue
          ? formatValueDate(newValue, formatValue)
          : undefined;
      }
    }
    setInnerValue(finalValue);
    onChange(finalValue, metadata);
  };

  const hasValue = () => {
    if (variant === "range") {
      if (value) {
        return Boolean(value.from ?? value.to);
      }
      if (innerValue) {
        return Boolean(innerValue.from ?? innerValue.to);
      }
    }
  };

  const disableDate = useCallback(
    (day) => {
      if (availableDates) {
        return !availableDates.some((availableDay) =>
          moment(availableDay).isSame(day, "day")
        );
      } else {
        return false;
      }
    },
    [availableDates]
  );

  const clear = () => {
    setInnerValue(variant === "range" ? { from: null, to: null } : null);
    onChange(variant === "range" ? { from: null, to: null } : null);
  };

  return {
    innerValue,
    displayedText,
    displayedRangeText,
    defaultMonth,
    isOpen,
    anchorEl,
    handleOpen,
    handleClose,
    handleChange,
    disableDate,
    hasValue,
    clear,
  };
};

export default useDatePickerState;
